import { FC, MouseEvent, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/Modal";
import { Actions, CloseButton, StyledDialog } from "../AlertDialogs/Dialog";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import Text from "../Text";
import Button from "../Button";
import { Input } from "../Forms";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { ChannelsListResource } from "@/types/resources";

const StyledHeading = styled(Text)`
  margin-bottom: 24px;
`;

interface Props {
  show: boolean;
  onClose: () => void;
  selectableChannels: Array<{
    id: number;
  }>;
}

const ChannelListModal: FC<Props> = ({ show, onClose, selectableChannels }) => {
  const ref = useRef<HTMLDivElement>(null);

  const alerts = useAlerts();

  const { teamId } = useParams();

  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
    onClose();
  };

  useOnClickOutside(ref, onClose);

  const queryClient = useQueryClient();

  const {
    register,
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = handleSubmit(async ({ name }) => {
    try {
      const list = await api.post<
        { name: string; channels: number[] },
        ChannelsListResource
      >(`/v1/teams/${teamId}/channels-lists`, {
        name,
        channels: selectableChannels.map((channel) => channel.id),
      });

      queryClient.setQueryData(
        ["channels_lists", { teamId }],
        (lists: ChannelsListResource[] | undefined) => [...(lists || []), list]
      );

      onClose();

      alerts.success(
        "Channels list shortcut was successfully created",
        list.name
      );
    } catch (err) {
      alerts.error();
    }
  });

  return (
    <AnimatePresence>
      {show && (
        <Modal zIndex={50}>
          <StyledDialog
            ref={ref}
            initial={{ scale: 0.3 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.3 }}
          >
            <CloseButton onClick={onCloseWithEventPrevention}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13 13L1 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
            <form onSubmit={onSubmit}>
              <StyledHeading $type="heading2" as="h4">
                Name of channels list
              </StyledHeading>
              <Input
                {...register("name", { required: true })}
                autoComplete="off"
                placeholder="Enter the name of channels list"
              />
              <Actions>
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
                <Button onClick={onCloseWithEventPrevention} color="secondary">
                  Cancel
                </Button>
              </Actions>
            </form>
          </StyledDialog>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default ChannelListModal;
